import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../services/authService';
import { SidebarConfig, MenuItem, MenuSection, SpecialHandler } from '../types/sidebarTypes';
import moduleConfigs from '../config/moduleConfigs.json';
import './Sidebar.css';

interface SidebarProps {
  user: User | null;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onSignOut: () => void;
  onShowAuthModal: () => void;
  authLoading: boolean;
  currentPath?: string;
}

const DEFAULT_MODULES = ['lifetime', 'habits', 'goals', 'today', 'tasks'];

const Sidebar: React.FC<SidebarProps> = ({
  user,
  sidebarOpen,
  onToggleSidebar,
  onSignOut,
  onShowAuthModal,
  authLoading,
  currentPath = ''
}) => {
  const navigate = useNavigate();
  const config: SidebarConfig = moduleConfigs as SidebarConfig;

  // Get user's selected modules or use defaults
  const getSelectedModules = (): string[] => {
    if (!user) return [];
    const userModules = user.module_configs;
    return userModules && userModules.length > 0 ? userModules : DEFAULT_MODULES;
  };

  const selectedModules = getSelectedModules();

  // Filter sections to only include selected modules
  const getFilteredSections = (): MenuSection[] => {
    return config.menuSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => selectedModules.includes(item.id))
      }))
      .filter(section => section.items.length > 0 || section.id === 'home');
  };

  const filteredSections = getFilteredSections();

  const navigateAndClose = (path: string) => {
    navigate(path);
    onToggleSidebar();
  };

  const handleSpecialNavigation = (handler: SpecialHandler) => {
    switch (handler) {
      case 'navigateToToday':
        const today = new Date().toISOString().split('T')[0];
        navigateAndClose(`/days/${today}`);
        break;
      default:
        break;
    }
  };

  const isItemActive = (item: MenuItem): boolean => {
    return item.activePattern.some(pattern => {
      if (pattern === '/' || pattern === '') {
        return currentPath === pattern;
      }
      return currentPath.startsWith(pattern);
    });
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.isSpecial && item.specialHandler) {
      handleSpecialNavigation(item.specialHandler);
    } else {
      navigateAndClose(item.path);
    }
  };

  const renderMenuItem = (item: MenuItem) => (
    <button 
      key={item.id}
      className={`menu-item-btn ${isItemActive(item) ? 'active' : ''}`}
      onClick={() => handleItemClick(item)}
    >
      {item.label}
    </button>
  );

  const renderMenuSection = (section: MenuSection) => (
    <div key={section.id} className="menu-section">
      {section.title && (
        <div className="menu-section-header">{section.title}</div>
      )}
      <div className={section.title ? "menu-section-items" : ""}>
        {section.items.map(renderMenuItem)}
      </div>
    </div>
  );

  const renderHomeButton = () => {
    if (!user) return null;
    
    // Always show Home button, navigate to /today
    const today = new Date().toISOString().split('T')[0];
    const isActive = currentPath === '/' || currentPath === '' || currentPath.startsWith('/days');
    
    return (
      <button 
        className={`sidebar-home-btn ${isActive ? 'active' : ''}`}
        onClick={() => navigateAndClose(`/days/${today}`)}
      >
        🏠 Home
      </button>
    );
  };

  const renderDashboardButton = () => {
    if (!user) return null;
    
    const isActive = currentPath === '/dashboard' || currentPath.startsWith('/dashboard');
    
    return (
      <button 
        className={`sidebar-dashboard-btn ${isActive ? 'active' : ''}`}
        onClick={() => navigateAndClose('/dashboard')}
        title="Dashboard"
      >
        📊
      </button>
    );
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-left">
            {user && renderHomeButton()}
            {user && renderDashboardButton()}
          </div>
          <div className="sidebar-header-right">
            {user && (
              <button 
                className="sidebar-settings-btn"
                onClick={() => navigateAndClose('/settings')}
                title="Settings"
              >
                ⚙️
              </button>
            )}
            <button className="sidebar-close" onClick={onToggleSidebar}>×</button>
          </div>
        </div>
        <div className="sidebar-content">
          {user ? (
            <div className="sidebar-menu">
              {/* Dynamic Menu Sections */}
              {filteredSections
                .filter(section => section.id !== 'home') // Exclude home section as it's rendered in header
                .map(renderMenuSection)}
            </div>
          ) : (
            <div className="auth-prompt">
              <p>Sign in to access your library</p>
              <button 
                className="signin-btn" 
                onClick={onShowAuthModal}
                disabled={authLoading}
              >
                {authLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    🔑 Sign In
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={onToggleSidebar}></div>}
    </>
  );
};

export default Sidebar;
